/**
 * StagingOutputCard — Green-accented card showing staging pipeline results.
 *
 * Rendered on BatchDetail when `batch.status === 'staged'`.
 * Shows output name, row/col counts, pipeline step summary, and action buttons.
 */
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  AccountTree as PipelineIcon,
  TableChart as PreviewIcon,
  Storage as RowsIcon,
  ViewColumn as ColsIcon,
  AutoFixHigh as StepsIcon,
} from '@mui/icons-material';

// ── Types ────────────────────────────────────────────────────────────

interface StagingOutputCardProps {
  /** Name of the staging output (e.g. "cleaned_vendors") */
  outputName?: string;
  /** Number of rows in the staging output */
  rowCount?: number;
  /** Number of columns in the staging output */
  colCount?: number;
  /** Number of pipeline steps that produced this output */
  stepCount?: number;
  /** Summary of changes applied (e.g. "3 transforms, 1 enrichment") */
  changesSummary?: string;
  /** Navigate to the pipeline canvas view */
  onViewPipeline?: () => void;
  /** Preview the staging output data */
  onPreviewOutput?: () => void;
}

// ── Stat widget ──────────────────────────────────────────────────────

const StatItem: React.FC<{
  icon: React.ReactElement;
  label: string;
  value: string | number;
}> = ({ icon, label, value }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ color: 'text.secondary', mb: 0.25 }}>{icon}</Box>
    <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
      {label}
    </Typography>
  </Box>
);

// ── Component ────────────────────────────────────────────────────────

const StagingOutputCard: React.FC<StagingOutputCardProps> = ({
  outputName,
  rowCount,
  colCount,
  stepCount,
  changesSummary,
  onViewPipeline,
  onPreviewOutput,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        mb: 3,
        borderLeft: `6px solid ${theme.palette.success.main}`,
        bgcolor: alpha(theme.palette.success.main, 0.02),
      }}
      elevation={2}
    >
      <CardContent sx={{ py: 2 }}>
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <SuccessIcon sx={{ color: 'success.main', fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Staging Output Ready
            </Typography>
            {outputName && (
              <Typography variant="body2" color="text.secondary">
                <Box
                  component="span"
                  sx={{ fontFamily: 'monospace', fontSize: 12, bgcolor: 'grey.100', px: 0.75, py: 0.25, borderRadius: 0.5 }}
                >
                  {outputName}
                </Box>
              </Typography>
            )}
          </Box>
          <Chip
            label="Staged"
            color="success"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Stats row */}
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 1.5 }}>
          {rowCount != null && (
            <Grid item>
              <StatItem
                icon={<RowsIcon sx={{ fontSize: 20 }} />}
                label="Rows"
                value={rowCount}
              />
            </Grid>
          )}
          {colCount != null && (
            <Grid item>
              <StatItem
                icon={<ColsIcon sx={{ fontSize: 20 }} />}
                label="Columns"
                value={colCount}
              />
            </Grid>
          )}
          {stepCount != null && (
            <Grid item>
              <StatItem
                icon={<StepsIcon sx={{ fontSize: 20 }} />}
                label="Pipeline Steps"
                value={stepCount}
              />
            </Grid>
          )}
        </Grid>

        {/* Changes summary */}
        {changesSummary && (
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.06),
              border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
              mb: 1.5,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {changesSummary}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {onViewPipeline && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PipelineIcon sx={{ fontSize: 16 }} />}
              onClick={onViewPipeline}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              View Pipeline
            </Button>
          )}
          {onPreviewOutput && (
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<PreviewIcon sx={{ fontSize: 16 }} />}
              onClick={onPreviewOutput}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              Preview Output
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default StagingOutputCard;
